import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Search, Plus, Pencil, Trash2, ChevronRight, X, Check, ChevronsUpDown } from 'lucide-react';
import { formatQuantity, formatCost } from '../utils/formatters';

const SubRecipesPage = () => {
  const { isAdmin } = useAuth();
  const [subRecipes, setSubRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [openComponentSearch, setOpenComponentSearch] = useState(null);
  const [allSubRecipesDropdown, setAllSubRecipesDropdown] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '', yield_amount: '', yield_type: 'g',
    cook_yield_percentage: '100', instructions: '', sop: '', ingredients: []
  });

  const fetchSubRecipes = useCallback(async () => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/sub-recipes`, { params });
      setSubRecipes(response.data);
    } catch (error) { toast.error('Failed to fetch sub-recipes'); }
    finally { setLoading(false); }
  }, [search]);

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) { toast.error('Failed to fetch ingredients'); }
  };

  const fetchAllForDropdown = async () => {
    try {
      const [ingRes, srRes] = await Promise.all([
        axios.get(`${API}/ingredients`),
        axios.get(`${API}/sub-recipes`),
      ]);
      setIngredients(ingRes.data);
      setAllSubRecipesDropdown(srRes.data);
    } catch (error) { /* ignore - non-blocking */ }
  };

  useEffect(() => { fetchSubRecipes(); fetchAllForDropdown(); }, [fetchSubRecipes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        yield_amount: parseFloat(formData.yield_amount),
        cook_yield_percentage: parseFloat(formData.cook_yield_percentage),
        ingredients: formData.ingredients.map(ing => ({
          ...ing, weight: parseFloat(ing.weight)
        }))
      };
      if (editingRecipe) {
        await axios.put(`${API}/sub-recipes/${editingRecipe.id}`, payload);
        toast.success('Sub-recipe updated');
      } else {
        await axios.post(`${API}/sub-recipes`, payload);
        toast.success('Sub-recipe created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSubRecipes();
      // Refresh selected recipe detail
      if (editingRecipe) {
        const res = await axios.get(`${API}/sub-recipes/${editingRecipe.id}`);
        setSelectedRecipe(res.data);
      }
    } catch (error) { toast.error('Failed to save sub-recipe'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sub-recipe?')) return;
    try {
      await axios.delete(`${API}/sub-recipes/${id}`);
      toast.success('Sub-recipe deleted');
      if (selectedRecipe?.id === id) setSelectedRecipe(null);
      fetchSubRecipes();
    } catch (error) { toast.error('Failed to delete sub-recipe'); }
  };

  const openEditDialog = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      name: recipe.name,
      yield_amount: recipe.yield_amount.toString(),
      yield_type: recipe.yield_type,
      cook_yield_percentage: recipe.cook_yield_percentage.toString(),
      instructions: recipe.instructions || '',
      sop: recipe.sop || '',
      ingredients: JSON.parse(JSON.stringify(recipe.ingredients)).map(ing => ({ ...ing, _uid: ing._uid || crypto.randomUUID() }))
    });
    setIsDialogOpen(true);
    fetchAllForDropdown(); // Refresh dropdown data for real-time accuracy
  };

  const resetForm = () => {
    setEditingRecipe(null);
    setFormData({
      name: '', yield_amount: '', yield_type: 'g',
      cook_yield_percentage: '100', instructions: '', sop: '', ingredients: []
    });
  };

  const addIngredientToForm = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, {
        _uid: crypto.randomUUID(),
        ingredient_id: '', ingredient_name: '', weight: '', weight_type: 'g',
        component_type: 'ingredient' // ingredient or sub_recipe
      }]
    });
  };

  const updateFormIngredient = (index, updates) => {
    setFormData(prev => {
      const updated = [...prev.ingredients];
      updated[index] = { ...updated[index], ...updates };
      return { ...prev, ingredients: updated };
    });
  };

  const removeFormIngredient = (index) => {
    setFormData(prev => ({
      ...prev, ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  // Build sorted lists for component dropdown (always uses full unfiltered data)
  const sortedIngredients = [...ingredients].sort((a, b) => a.name.localeCompare(b.name));
  const sortedSubRecipes = [...allSubRecipesDropdown].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="sub-recipes-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
          Sub-Recipes
        </h1>
        {isAdmin && (
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="btn-primary px-6 py-2 font-medium" data-testid="add-sub-recipe-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Sub-Recipe
          </Button>
        )}
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input type="text" placeholder="Search sub-recipes..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="sub-recipe-search-input" />
        </div>
      </div>

      {loading && <div className="progress-bar mb-4"><div className="progress-bar-fill"></div></div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 lg:col-span-1 max-h-[700px] overflow-y-auto" style={{ borderRadius: '12px' }}>
          <div className="sticky top-0 bg-gray-50 px-4 py-3 border-b border-gray-200">
            <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">
              Sub-Recipes ({subRecipes.length})
            </span>
          </div>
          {subRecipes.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No sub-recipes found.</div>
          ) : (
            subRecipes.map((recipe) => (
              <div key={recipe.id} onClick={() => setSelectedRecipe(recipe)}
                className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
                  selectedRecipe?.id === recipe.id ? 'bg-[var(--color-primary-50)]' : 'hover:bg-gray-50'
                }`} data-testid={`sub-recipe-item-${recipe.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-sm">{recipe.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Yield: {formatQuantity(recipe.yield_amount)}{recipe.yield_type}
                      {recipe.cook_yield_percentage !== 100 && ` | Cook Yield: ${recipe.cook_yield_percentage}%`}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 mt-1" />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="bg-white border border-gray-200 lg:col-span-2 p-6 overflow-y-auto" style={{ borderRadius: '12px', minHeight: '400px', maxHeight: '700px' }}>
          {selectedRecipe ? (
            <div data-testid="sub-recipe-detail">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                  {selectedRecipe.name}
                </h2>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEditDialog(selectedRecipe)}
                      className="p-2 border border-gray-200 hover:bg-gray-100 rounded-lg" data-testid="edit-sub-recipe-btn">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(selectedRecipe.id)}
                      className="p-2 border border-gray-200 hover:bg-red-50 text-red-600 rounded-lg" data-testid="delete-sub-recipe-btn">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Yield</div>
                  <div className="font-semibold tabular-nums">{formatQuantity(selectedRecipe.yield_amount)}{selectedRecipe.yield_type}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Cook Yield</div>
                  <div className="font-semibold tabular-nums">{selectedRecipe.cook_yield_percentage}%</div>
                </div>
                {isAdmin && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Cost/g</div>
                    <div className="font-semibold tabular-nums">£{selectedRecipe.cost_per_unit?.toFixed(4) || '0.0000'}</div>
                  </div>
                )}
              </div>

              {isAdmin && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Total Cost</div>
                    <div className="font-semibold tabular-nums">£{selectedRecipe.total_ingredient_cost?.toFixed(2) || '0.00'}</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Cost per 100g</div>
                    <div className="font-semibold tabular-nums">£{((selectedRecipe.cost_per_unit || 0) * 100).toFixed(2)}</div>
                  </div>
                </div>
              )}

              {selectedRecipe.sop && selectedRecipe.sop.trim() && (
                <div className="mb-6 p-4 border-l-4 border-amber-400 bg-amber-50 rounded" data-testid="sub-recipe-sop-display">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">SOP</div>
                  <div className="text-sm whitespace-pre-wrap text-gray-800">{selectedRecipe.sop}</div>
                </div>
              )}

              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Ingredients ({selectedRecipe.ingredients?.length || 0})
              </h3>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Type</th>
                      <th>Weight</th>
                      {isAdmin && <th>Cost</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRecipe.ingredients?.map((ing) => (
                      <tr key={ing.ingredient_name}>
                        <td className="font-medium text-sm">{ing.ingredient_name}</td>
                        <td><span className="badge-pill badge-gray text-[10px]">{ing.component_type === 'sub_recipe' ? 'Sub-Recipe' : 'Ingredient'}</span></td>
                        <td className="tabular-nums">{formatQuantity(ing.weight)} {ing.weight_type}</td>
                        {isAdmin && <td className="tabular-nums">{ing.cost ? `£${ing.cost.toFixed(2)}` : '£0.00'}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400" style={{ minHeight: '400px' }}>
              <p className="text-sm">Select a sub-recipe to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" style={{ borderRadius: '12px' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {editingRecipe ? 'Edit Sub-Recipe' : 'Add New Sub-Recipe'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Sub-Recipe Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Cheese Sauce" required data-testid="sub-recipe-name-input" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label>Yield Amount</Label>
                  <Input type="number" step="0.01" value={formData.yield_amount}
                    onChange={(e) => setFormData({ ...formData, yield_amount: e.target.value })}
                    placeholder="1000" className="tabular-nums" required data-testid="sub-recipe-yield-input" />
                </div>
                <div className="grid gap-2">
                  <Label>Yield Type</Label>
                  <Select value={formData.yield_type} onValueChange={(v) => setFormData({ ...formData, yield_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="g">Grams (g)</SelectItem>
                      <SelectItem value="kg">Kilograms (kg)</SelectItem>
                      <SelectItem value="count">Count</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Cook Yield %</Label>
                  <Input type="number" step="1" value={formData.cook_yield_percentage}
                    onChange={(e) => setFormData({ ...formData, cook_yield_percentage: e.target.value })}
                    className="tabular-nums" required data-testid="sub-recipe-cook-yield-input" />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>SOP (Standard Operating Procedure)</Label>
                <Textarea value={formData.sop}
                  onChange={(e) => setFormData({ ...formData, sop: e.target.value })}
                  placeholder="Prep and cooking notes for kitchen reference..."
                  className="min-h-[90px]" data-testid="sub-recipe-sop-input" />
              </div>

              <div className="grid gap-2">
                <Label>Instructions</Label>
                <Textarea value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  placeholder="Cooking instructions..." className="min-h-[60px]" data-testid="sub-recipe-instructions-input" />
              </div>

              {/* Components Section */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-500">
                    Components
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={addIngredientToForm}
                    className="text-xs" style={{ borderRadius: '9999px' }} data-testid="add-ingredient-to-sub-recipe-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Component
                  </Button>
                </div>
                
                {formData.ingredients.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4">
                    No components. Add ingredients or sub-recipes.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.ingredients.map((ing, idx) => (
                      <div key={ing._uid || ing.ingredient_name || idx} className="p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid={`sub-recipe-component-${idx}`}>
                        {/* Row 1: Type selector + searchable component picker */}
                        <div className="flex gap-2 mb-2">
                          <Select value={ing.component_type || 'ingredient'}
                            onValueChange={(v) => updateFormIngredient(idx, { component_type: v, ingredient_id: '', ingredient_name: '' })}>
                            <SelectTrigger className="w-[140px] text-xs h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ingredient">Ingredient</SelectItem>
                              <SelectItem value="sub_recipe">Sub-Recipe</SelectItem>
                            </SelectContent>
                          </Select>
                          <Popover open={openComponentSearch === idx} onOpenChange={(open) => setOpenComponentSearch(open ? idx : null)}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="flex-1 justify-between text-sm font-normal h-9">
                                <span className="truncate">{ing.ingredient_name || 'Search component...'}</span>
                                <ChevronsUpDown className="w-3 h-3 ml-1 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[400px] p-0" style={{ borderRadius: '12px' }}>
                              <Command>
                                <CommandInput placeholder={`Search ${ing.component_type === 'sub_recipe' ? 'sub-recipes' : 'ingredients'}...`} />
                                <CommandList>
                                  <CommandEmpty>Not found.</CommandEmpty>
                                  <CommandGroup>
                                    {(ing.component_type === 'sub_recipe' ? sortedSubRecipes : sortedIngredients).map(item => (
                                      <CommandItem key={item.id} value={item.name} onSelect={() => {
                                        updateFormIngredient(idx, { ingredient_id: item.id, ingredient_name: item.name });
                                        setOpenComponentSearch(null);
                                      }}>
                                        <Check className={`w-4 h-4 mr-2 shrink-0 ${ing.ingredient_id === item.id ? 'opacity-100' : 'opacity-0'}`} />
                                        <span className="truncate">{item.name}</span>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <button type="button" onClick={() => removeFormIngredient(idx)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Row 2: Weight + type */}
                        <div className="flex gap-2">
                          <div className="w-[120px]">
                            <Input type="number" step="0.01" value={ing.weight}
                              onChange={(e) => updateFormIngredient(idx, { weight: e.target.value })}
                              placeholder="Weight" className="text-sm h-9" />
                          </div>
                          <Select value={ing.weight_type} onValueChange={(v) => updateFormIngredient(idx, { weight_type: v })}>
                            <SelectTrigger className="w-[90px] text-xs h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="g">g</SelectItem>
                              <SelectItem value="kg">kg</SelectItem>
                              <SelectItem value="count">count</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="btn-outline">Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-sub-recipe-btn">
                {editingRecipe ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubRecipesPage;
